// One login for khanesarmaye.com, bank.khanesarmaye.com (Sheba) and app.khanesarmaye.com (Tether web):
// the access and refresh tokens live in cookies on .khanesarmaye.com, so logging in or out on one
// site applies to all of them. KEEP IN SYNC with bank-nextjs src/utils/profile/authSession.ts and the
// cookie handling in TetherAppFinal web/src/hooks/useAuth.tsx (same names, same logout marker).
import { destroyCookie, parseCookies, setCookie } from 'nookies';

export const AUTH_COOKIE = 'user_auth_token';
export const REFRESH_COOKIE = 'user_refresh_token';
/** Set on logout: a token kept in another site's localStorage older than this must not log the user back in. */
export const LOGOUT_MARKER = 'khs_logged_out_at';
const SAVED_AT_KEY = 'user_auth_saved_at';
const PROFILE_CACHE_KEY = 'cached_user_profile';
const MAX_AGE = 30 * 24 * 60 * 60;

const isLocalhost = () =>
    typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const cookieDomain = () => (isLocalhost() ? undefined : '.khanesarmaye.com');
const clean = (v?: string | null) => (v && v !== 'undefined' && v !== 'null' ? v : null);

const local = {
    get: (k: string) => {
        try {
            return localStorage.getItem(k);
        } catch {
            return null;
        }
    },
    set: (k: string, v: string) => {
        try {
            localStorage.setItem(k, v);
        } catch {
            // storage blocked
        }
    },
    remove: (k: string) => {
        try {
            localStorage.removeItem(k);
        } catch {
            // storage blocked
        }
    },
};

const writeCookie = (name: string, value: string, maxAge = MAX_AGE) =>
    setCookie(null, name, value, {
        maxAge,
        path: '/',
        domain: cookieDomain(),
        secure: !isLocalhost(),
        sameSite: 'lax',
    });

/** After login or refresh. A refresh token is only written when the server sent one. */
export const saveAuthSession = (token: string, refreshToken?: string | null) => {
    writeCookie(AUTH_COOKIE, token);
    local.set(AUTH_COOKIE, token);
    if (refreshToken) {
        writeCookie(REFRESH_COOKIE, refreshToken);
        local.set(REFRESH_COOKIE, refreshToken);
    }
    local.set(SAVED_AT_KEY, String(Date.now()));
};

/** Logout on any of the sites: both tokens gone everywhere, plus the marker for the others' localStorage. */
export const clearAuthSession = () => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    // 'token' / 'refresh_token': names an older Tether web build wrote on .khanesarmaye.com.
    for (const name of [AUTH_COOKIE, REFRESH_COOKIE, 'token', 'refresh_token']) {
        for (const domain of [
            undefined,
            '.khanesarmaye.com',
            'khanesarmaye.com',
            host,
            host ? `.${host}` : undefined,
        ]) {
            destroyCookie(null, name, { path: '/', ...(domain ? { domain } : {}) });
        }
        local.remove(name);
    }
    local.remove(SAVED_AT_KEY);
    local.remove(PROFILE_CACHE_KEY);
    writeCookie(LOGOUT_MARKER, String(Date.now()));
};

/** The shared cookie wins; localStorage is only a fallback when the cookie was evicted, never after a logout elsewhere. */
const read = (name: string): string | null => {
    const fromCookie = clean(parseCookies()[name]);
    if (fromCookie) {
        if (local.get(name) !== fromCookie) local.set(name, fromCookie);
        return fromCookie;
    }

    const fromLocal = clean(local.get(name));
    if (!fromLocal) return null;

    const loggedOutAt = Number(parseCookies()[LOGOUT_MARKER]) || 0;
    const savedAt = Number(local.get(SAVED_AT_KEY)) || 0;
    if (loggedOutAt && loggedOutAt >= savedAt) {
        local.remove(name);
        return null;
    }

    writeCookie(name, fromLocal);
    return fromLocal;
};

export const getAuthToken = (): string | null => read(AUTH_COOKIE);
export const getRefreshToken = (): string | null => read(REFRESH_COOKIE);

export const cacheProfile = (user: unknown) => local.set(PROFILE_CACHE_KEY, JSON.stringify(user));
export const cachedProfile = <T = any>(): T | null => {
    try {
        return JSON.parse(local.get(PROFILE_CACHE_KEY) || 'null');
    } catch {
        return null;
    }
};
